import { UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { DashboardActivity } from '@/api/dashboard';
import { EmptyState } from '@/components/ui/EmptyState';
import { Eyebrow } from '@/components/ui/Eyebrow';
import { Table, type Column } from '@/components/ui/Table';
import { type } from '@/theme/typography';

import { Glued } from './Glued';

interface Props {
  activity: DashboardActivity;
}

type ActivityRow = DashboardActivity['rows'][number];

/**
 * `DESIGN.md` §6.2, Section 5 — Interviewer activity. **Admin only, and throughput only.**
 *
 * ⚠️ **This table must never show an interviewer's average score, their score distribution, their
 * recommendation mix, or any ranking by one of them** (ADR 0015 §1; §6.2 §5). It shows *whether
 * people write their feedback up*, never *what they concluded*. The moment "Ivan averages 4.2, Anna
 * averages 3.1" is visible to a manager, both of them start scoring toward whatever they believe is
 * expected and the scores stop being opinions — the exact failure the seven protections exist to
 * prevent, arriving through a reporting screen instead of a data model.
 *
 * This is the edit someone will request, in good faith, as *"it would be useful to see who scores
 * harshly"*. **The answer is no, permanently.** The column that would satisfy the request is the
 * column that destroys the data. `sections.test.tsx` asserts the rendered table carries no such
 * column, so a well-meaning addition fails the suite rather than shipping.
 *
 * Admin-only is decided **in the payload**: a Recruiter's body simply has no
 * `interviewer_activity`, so this component is never rendered for them — no empty frame and no
 * "you do not have access" (the API never answers a status code for it).
 *
 * The rows arrive sorted by drafts outstanding, descending — §6.2's actionable order. The screen
 * does not re-sort them.
 */
export function InterviewerActivitySection({ activity }: Props) {
  // `common` is not in this list any more: the em dash for a `null` median moved into `Glued` with
  // the unit (BUG-092), and nothing else on this table reads a shared string.
  const { t } = useTranslation('dashboard');

  const columns: Array<Column<ActivityRow>> = [
    {
      key: 'interviewer',
      header: t('dashboard:activity.columns.interviewer'),
      primary: true,
      cell: (row) => row.interviewer,
    },
    {
      key: 'interviews_submitted',
      header: t('dashboard:activity.columns.submitted'),
      align: 'right',
      cell: (row) => <span className={type.numeral}>{row.interviews_submitted}</span>,
    },
    {
      key: 'median_turnaround',
      /*
       * ⚠️ **The unit is glued to the figure, exactly as §6.2 Section 2 does it** (BUG-092,
       * 2026-09-28). This cell rendered `15.5` — a bare number, with no unit on the figure and none in
       * the header either, so nothing on screen told a reader whether the median was days or hours.
       * It is the **same quantity** Section 2 renders two sections higher as `38h`, and the same
       * argument TASK-152 made there applies unchanged: a bare number invites *"15.5 what?"*. §6.2
       * now says which of the two answers this column takes, because the two sections answered one
       * question differently until today.
       *
       * `Glued` keeps the letter `aria-hidden` and speaks *"15.5 hours"* beside it, so a table cell
       * is not read out as a number and a letter. The `null` case is unchanged — `—`, never `0h`,
       * because a unit on a measurement nobody took is worse than the bare number this replaces.
       */
      header: t('dashboard:activity.columns.turnaround'),
      align: 'right',
      cell: (row) => (
        <span className={type.numeral}>
          <Glued value={row.median_turnaround} unit="hours" />
        </span>
      ),
    },
    {
      key: 'drafts_outstanding',
      header: t('dashboard:activity.columns.drafts'),
      align: 'right',
      cell: (row) => <span className={type.numeral}>{row.drafts_outstanding}</span>,
    },
  ];

  return (
    <section className="mt-10" aria-label={t('dashboard:activity.title')}>
      <Eyebrow count={activity.rows.length}>{t('dashboard:activity.title')}</Eyebrow>
      <p className={`${type.metadata} -mt-2 mb-4`}>{t('dashboard:activity.note')}</p>

      <Table
        columns={columns}
        rows={activity.rows}
        rowKey={(row) => row.interviewer_id}
        empty={<EmptyState icon={UserRound} message={t('dashboard:activity.empty')} />}
      />
    </section>
  );
}
